import BuilderGuard from "helpers/AuthType/BuilderGuard";
import React, { useState } from "react";
import {
    Container,
    Row,
    Col,
    Card,
    CardBody,
    Button,
    Progress,
    Badge
} from "reactstrap";
import Select from "react-select";
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    Tooltip,
    ResponsiveContainer,
    PieChart,
    Pie,
    Cell,
    BarChart,
    Bar
} from "recharts";

/* ------------------ THEME ------------------ */
const theme = {
    accent: "#6f42c1",
    glassBg: "rgba(255,255,255,0.08)",
    border: "1px solid rgba(255,255,255,0.2)",
    glow: "0 0 25px rgba(111,66,193,0.5)",
    textMuted: "rgba(255,255,255,0.7)"
};

const glassCard = {
    background: theme.glassBg,
    backdropFilter: "blur(12px)",
    borderRadius: "18px",
    border: theme.border,
    color: "#fff",
    boxShadow: theme.glow,
    transition: "all 0.25s ease"
};

/* ------------------ DUMMY DATA ------------------ */
const trendWeekly = [
    { label: "Mon", walkings: 4 },
    { label: "Tue", walkings: 7 },
    { label: "Wed", walkings: 5 },
    { label: "Thu", walkings: 9 },
    { label: "Fri", walkings: 6 },
    { label: "Sat", walkings: 8 },
    { label: "Sun", walkings: 3 }
];

const trendMonthly = [
    { label: "Week 1", walkings: 20 },
    { label: "Week 2", walkings: 28 },
    { label: "Week 3", walkings: 24 },
    { label: "Week 4", walkings: 35 }
];

const warmthData = [
    { name: "Hot", value: 8 },
    { name: "Warm", value: 12 },
    { name: "Cold", value: 5 }
];

const projectData = {
    all: [
        { project: "Sky Towers", walkings: 10 },
        { project: "Palm Residency", walkings: 6 },
        { project: "Lake View", walkings: 9 },
        { project: "Urban Nest", walkings: 4 }
    ],
    sky: [{ project: "Sky Towers", walkings: 10 }],
    palm: [{ project: "Palm Residency", walkings: 6 }],
    lake: [{ project: "Lake View", walkings: 9 }]
};

const projectOptions = [
    { value: "all", label: "All Projects" },
    { value: "sky", label: "Sky Towers" },
    { value: "palm", label: "Palm Residency" },
    { value: "lake", label: "Lake View" }
];

const COLORS = ["#ff4d4f", "#faad14", "#40a9ff"];

/* ------------------ KPI COMPONENT ------------------ */
const KPICard = ({ icon, title, value, subtitle, badge, progress }) => (
    <Card
        style={glassCard}
        onMouseEnter={(e) => {
            e.currentTarget.style.transform = "translateY(-6px)";
            e.currentTarget.style.boxShadow = "0 0 40px rgba(111,66,193,0.9)";
        }}
        onMouseLeave={(e) => {
            e.currentTarget.style.transform = "translateY(0)";
            e.currentTarget.style.boxShadow = theme.glow;
        }}
    >
        <CardBody>
            <div
                style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    marginBottom: "10px"
                }}
            >
                <h6 style={{ margin: 0 }}>{title}</h6>
                <i className={`bx ${icon}`} style={{ fontSize: "22px", color: theme.accent }} />
            </div>

            <h2 style={{ marginBottom: "5px" }}>{value}</h2>
            <span style={{ color: theme.textMuted }}>{subtitle}</span>

            {progress && (
                <Progress
                    value={progress}
                    className="mt-3"
                    style={{ height: "8px" }}
                    color="success"
                />
            )}

            {badge && (
                <Badge
                    color="success"
                    pill
                    className="mt-2"
                    style={{ background: theme.accent }}
                >
                    {badge}
                </Badge>
            )}
        </CardBody>
    </Card>
);

/* ------------------ MAIN ------------------ */
const BuilderDashboard = () => {
    const [trendType, setTrendType] = useState("weekly");
    const [selectedProject, setSelectedProject] = useState("all");

    const total = 25;
    const used = 18;
    const remaining = total - used;
    const conversionRate = ((5 / 8) * 100).toFixed(1);

    return (
        <BuilderGuard>
            <div className="page-content" style={{ color: "#fff" }}>
                <Container fluid>

                    {/* HEADER */}
                    <Row className="mb-4 align-items-center">
                        <Col md={6}>
                            <h2 style={{ fontWeight: "700", marginBottom: "4px" }}>
                                WAAS – Walkings as a Service
                            </h2>
                            <p style={{ color: theme.textMuted }}>
                                Builder Performance Dashboard
                            </p>
                        </Col>

                        {/* ACTION BAR */}
                        <Col md={6} className="d-flex justify-content-end gap-2">
                            <Button
                                color={trendType === "weekly" ? "primary" : "secondary"}
                                onClick={() => setTrendType("weekly")}
                            >
                                Weekly
                            </Button>
                            <Button
                                color={trendType === "monthly" ? "primary" : "secondary"}
                                onClick={() => setTrendType("monthly")}
                            >
                                Monthly
                            </Button>

                            <div style={{ minWidth: "220px" }}>
                                <Select
                                    options={projectOptions}
                                    defaultValue={projectOptions[0]}
                                    onChange={(e) => setSelectedProject(e.value)}
                                    styles={{
                                        control: (base) => ({
                                            ...base,
                                            background: theme.glassBg,
                                            border: theme.border,
                                            color: "#fff"
                                        }),
                                        singleValue: (base) => ({
                                            ...base,
                                            color: "#fff"
                                        }),
                                        menu: (base) => ({
                                            ...base,
                                            background: "#1E253A",
                                            color: "#fff"
                                        })
                                    }}
                                />
                            </div>
                        </Col>
                    </Row>

                    {/* KPI GRID */}
                    <Row className="mb-4">
                        <Col xl={3} md={6}>
                            <KPICard
                                icon="bx-walk"
                                title="Total Walkings"
                                value={total}
                                subtitle="Current Package"
                            />
                        </Col>

                        <Col xl={3} md={6}>
                            <KPICard
                                icon="bx-hot"
                                title="Hot / Warm / Cold"
                                value="8 / 12 / 5"
                                subtitle="Visitor Quality"
                            />
                        </Col>

                        <Col xl={3} md={6}>
                            <KPICard
                                icon="bx-pie-chart-alt-2"
                                title="Usage"
                                value={`${used} / ${remaining}`}
                                subtitle="Package Walkings"
                                progress={(used / total) * 100}
                            />
                        </Col>

                        <Col xl={3} md={6}>
                            <KPICard
                                icon="bx-trending-up"
                                title="Conversions"
                                value="5"
                                subtitle="Hot → Booking"
                                badge={`${conversionRate}% Success`}
                            />
                        </Col>
                    </Row>

                    {/* ANALYTICS */}
                    <Row className="mb-4">
                        <Col xl={8}>
                            <Card style={glassCard}>
                                <CardBody>
                                    <h5 className="mb-3">Walking Trend</h5>
                                    <ResponsiveContainer width="100%" height={300}>
                                        <LineChart
                                            data={
                                                trendType === "weekly"
                                                    ? trendWeekly
                                                    : trendMonthly
                                            }
                                        >
                                            <XAxis dataKey="label" stroke="#fff" />
                                            <YAxis stroke="#fff" />
                                            <Tooltip />
                                            <Line
                                                type="monotone"
                                                dataKey="walkings"
                                                stroke={theme.accent}
                                                strokeWidth={3}
                                                dot={{ r: 5 }}
                                                activeDot={{ r: 9 }}
                                            />
                                        </LineChart>
                                    </ResponsiveContainer>
                                </CardBody>
                            </Card>
                        </Col>

                        <Col xl={4}>
                            <Card style={glassCard}>
                                <CardBody>
                                    <h5 className="mb-3">Warmth Distribution</h5>
                                    <ResponsiveContainer width="100%" height={300}>
                                        <PieChart>
                                            <Pie
                                                data={warmthData}
                                                dataKey="value"
                                                nameKey="name"
                                                innerRadius={65}
                                                outerRadius={105}
                                                paddingAngle={5}
                                            >
                                                {warmthData.map((_, index) => (
                                                    <Cell key={index} fill={COLORS[index]} />
                                                ))}
                                            </Pie>
                                            <Tooltip />
                                        </PieChart>
                                    </ResponsiveContainer>
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>

                    {/* PROJECT + FEEDBACK */}
                    <Row>
                        <Col xl={8}>
                            <Card style={glassCard}>
                                <CardBody>
                                    <h5 className="mb-3">Project-wise Walkings</h5>
                                    <ResponsiveContainer width="100%" height={300}>
                                        <BarChart data={projectData[selectedProject]}>
                                            <XAxis dataKey="project" stroke="#fff" />
                                            <YAxis stroke="#fff" />
                                            <Tooltip />
                                            <Bar
                                                dataKey="walkings"
                                                fill="#40a9ff"
                                                radius={[8, 8, 0, 0]}
                                            />
                                        </BarChart>
                                    </ResponsiveContainer>
                                </CardBody>
                            </Card>
                        </Col>

                        <Col xl={4}>
                            <Card style={glassCard}>
                                <CardBody>
                                    <h5 className="mb-3">Feedback Rating</h5>
                                    <div
                                        style={{
                                            textAlign: "center",
                                            marginTop: "50px"
                                        }}
                                    >
                                        <h1>⭐ 4.3</h1>
                                        <p style={{ color: theme.textMuted }}>
                                            Average Visitor Rating
                                        </p>
                                        <Badge
                                            pill
                                            style={{
                                                background: theme.accent,
                                                padding: "8px 16px"
                                            }}
                                        >
                                            Excellent Performance
                                        </Badge>
                                    </div>
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                </Container>
            </div>
        </BuilderGuard>
    );
};

export default BuilderDashboard;
